import React, { useState, useRef } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { motion } from 'framer-motion';
import CircularProgress from '@mui/material/CircularProgress';
import { Send } from 'lucide-react';

const MessageInput = ({ onSendMessage }) => {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const theme = useTheme();
  const textareaRef = useRef(null);

  const isDark = theme?.mode === 'dark';
  const accentColor = theme?.colors?.button_light || '#fdbf00';
  const borderCol = isDark ? 'rgba(51, 65, 85, 0.4)' : '#e2e8f0';

  const handleTextChange = (e) => {
    setText(e.target.value);
    if (sendError) setSendError(null);
    
    // Auto-grow height logic
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!text.trim() || sending) return;
    
    setSending(true);
    setSendError(null);
    try {
      await onSendMessage(text.trim());
      setText('');
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    } catch (err) {
      console.error('Error sending message:', err);
      setSendError(err.message || 'No se pudo enviar el mensaje. Intenta enviar nuevamente.');
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault(); // Prevent adding a new line
      handleSubmit();
    }
  };

  return (
    <div 
      className="p-3 border-t transition-colors duration-300"
      style={{ 
        backgroundColor: isDark ? '#111e35' : '#f8fafc',
        borderColor: borderCol
      }}
    >
      <form 
        onSubmit={handleSubmit} 
        className="flex items-end gap-3 max-w-full relative"
      >
        <div className="flex-1 relative">
          <textarea
            ref={textareaRef}
            rows={1}
            value={text}
            onChange={handleTextChange}
            onKeyDown={handleKeyDown}
            placeholder="Escribe un mensaje..."
            className={`w-full rounded-2xl border px-4 py-3 text-xl focus:outline-none transition-all duration-300 custom-scrollbar resize-none ${
              isDark 
                ? 'bg-slate-800 text-slate-100 placeholder-slate-500 focus:ring-1 focus:ring-slate-600 focus:bg-slate-800' 
                : 'bg-white text-slate-800 placeholder-slate-400 focus:ring-1 focus:ring-slate-300 focus:bg-white'
            }`}
            style={{ 
              borderColor: sendError ? '#f43f5e' : borderCol,
              maxHeight: '120px',
              minHeight: '44px',
              lineHeight: '1.4'
            }}
            disabled={sending}
          />
        </div>
        
        <motion.button
          type="submit"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          disabled={!text.trim() || sending}
          className="rounded-2xl w-11 h-11 flex items-center justify-center text-white disabled:opacity-40 transition-colors shadow-md flex-shrink-0 cursor-pointer"
          style={{ 
            backgroundColor: accentColor,
            minWidth: '44px',
            minHeight: '44px'
          }}
        >
          {sending ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            <Send size={18} style={{ marginLeft: '1.5px' }} />
          )}
        </motion.button>
      </form>
      {sendError && (
        <div className="flex items-center gap-1.5 text-sm text-rose-500 font-medium px-1 pt-2">
          <span>⚠️</span>
          <span>{sendError}</span>
        </div>
      )}
    </div>
  );
};

export default MessageInput;
